import React, { useState, useMemo } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { Search, Building2, Calendar, ChevronDown, LogOut, ArrowUpDown, ArrowUp, ArrowDown, Settings, X, UserPlus, UserX, Check } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { AddUserFromACCDialog } from '@/components/AddUserFromACCDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';

interface MyHomeProject {
  id: string;
  name: string;
  hubId: string;
  hubName: string;
  status: string;
  number?: string;
  defaultAccess: string;
  account: string;
  createdOn: string;
  createdDate: Date;
  hasBimgoaiAccess?: boolean; // true = Active (clickable), false = Greyed Out (disabled)
  subscriptionStatus?: string; // Subscription status: 'active', 'trial', 'inactive', 'expired', etc.
  // Backend fields
  acc_project_id?: string;
  acc_hub_id?: string;
  acc_project_created_at?: string;
  createdAt?: string;
}

type SortField = 'name' | 'account' | 'createdOn' | 'subscription';
type SortDirection = 'asc' | 'desc';

const MyHomePage: React.FC = () => {
  const [, navigate] = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [accountFilter, setAccountFilter] = useState<string>('all'); // Keep for backward compatibility
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]); // Multi-select accounts
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedProjectForUsers, setSelectedProjectForUsers] = useState<MyHomeProject | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(false);
  const [showHubModal, setShowHubModal] = useState(false);
  const [hubFilter, setHubFilter] = useState<'active' | 'inactive' | 'all'>('all'); // Default to 'all' to show all projects regardless of subscription status
  const { user, logout } = useAuth();

  // Fetch user's accessible projects based on real OAuth permissions with subscription info
  const { data: projectsData, isLoading, error } = useQuery({
    queryKey: ['user-projects-oauth-v2'], // Use same key as Header to share cache
    queryFn: async () => {
      const { apiRequest } = await import('@/lib/queryClient');
      console.log('🔍 MyHomePage: Fetching projects from /api/autodesk/user-projects-with-subscription');
      
      // Add timeout wrapper for projects request (5 seconds)
      // Note: Since we're now reading from database instead of ACC API, 
      // the request should be much faster. 5 seconds is sufficient for database queries.
      // If it takes longer, there's likely a database connectivity issue.
      // Reduced from 10s to 5s to improve UX when token expires.
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Projects request timeout')), 5000);
      });
      
      const result = await Promise.race([
        apiRequest('/api/autodesk/user-projects-with-subscription'),
        timeoutPromise
      ]);
      
      return result;
    },
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors - redirect immediately
      if (error?.status === 401 || 
          error?.message?.includes('401') || 
          error?.message?.includes('Authentication required') ||
          error?.message?.includes('Unauthorized')) {
        return false;
      }
      // For other errors, only retry once (reduced from 2 to 1)
      // This reduces total wait time from 30s (10s * 3) to 10s (5s * 2)
      return failureCount < 1; 
    },
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache the data (React Query v5: cacheTime -> gcTime)
    refetchOnWindowFocus: false, 
  });

  // Handle error and redirect to login if authentication fails
  React.useEffect(() => {
    if (error) {
      const errorAny = error as any;
      const isAuthError = errorAny?.status === 401 || 
                          errorAny?.message?.includes('401') || 
                          errorAny?.message?.includes('Authentication required');
      
      if (isAuthError) {
        console.log(`🔧 MyHomePage: Authentication error, redirecting to login immediately`);
        localStorage.removeItem('sessionId');
        localStorage.removeItem('autodeskSessionId');
        // Redirect immediately without delay for better UX
        window.location.href = '/login';
      }
    }
  }, [error]);

  const projects: MyHomeProject[] = useMemo(() => {
    return (projectsData as any)?.results
      ?.filter((project: any) => project && project.id)
      ?.map((project: any, index: number) => {
        // DEBUG: Log first 3 projects to see what we receive from backend
        if (index < 3) {
          console.log(`🔍 [FRONTEND DEBUG] Project ${index + 1} (${project.name}):`, {
            acc_project_created_at: project.acc_project_created_at,
            createdOn: project.createdOn,
            createdAt: project.createdAt,
            added_at: project.added_at,
            allDateFields: {
              acc_project_created_at: project.acc_project_created_at,
              createdOn: project.createdOn,
              createdAt: project.createdAt,
              created_at: project.created_at,
              added_at: project.added_at,
              addedAt: project.addedAt
            }
          });
        }
        
        // Parse the creation date - check multiple possible locations
        // Priority order:
        // 1. acc_project_created_at (backend field from database acc_created_at column) - HIGHEST PRIORITY
        // 2. createdOn (backend formatted field)
        // 3. createdAt (top-level field)
        // 4. created_at (alternative top-level field)
        // 5. attributes.extension.data.createTime (Data Management API format)
        // 6. attributes.created_at (alternative nested field)
        let creationDate = null;
        const dateField = project.acc_project_created_at || 
                          project.createdOn ||
                          project.createdAt || 
                          project.created_at || 
                          project.attributes?.extension?.data?.createTime ||
                          project.attributes?.created_at;
        
        if (dateField) {
          const parsed = new Date(dateField);
          // Only validate that it's a valid date, no future date check
          if (!isNaN(parsed.getTime())) {
            creationDate = parsed;
            if (index < 3) {
              console.log(`✅ [FRONTEND DEBUG] Project ${index + 1} parsed date:`, {
                dateField,
                parsed: parsed.toISOString(),
                formatted: parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
              });
            }
          } else {
            if (index < 3) {
              console.warn(`⚠️ [FRONTEND DEBUG] Project ${index + 1} invalid date:`, dateField);
            }
          }
        } else {
          if (index < 3) {
            console.warn(`⚠️ [FRONTEND DEBUG] Project ${index + 1} no date field found`);
          }
        }
        
        // Only use fallback if creationDate is still null (missing date field)
        if (!creationDate) {
          // Try to use added_at if available (from backend, might be in different field names)
          const addedAtField = project.added_at || project.addedAt;
          if (addedAtField) {
            const addedAtParsed = new Date(addedAtField);
            if (!isNaN(addedAtParsed.getTime())) {
              creationDate = addedAtParsed;
              console.log(`ℹ️ [MYHOME] Using added_at as fallback for project: ${project.name}`);
            }
          }
          
          // Final fallback if still no valid date
          if (!creationDate) {
            console.warn("⚠️ [MYHOME] Missing date for project:", project.name, project.id, {
              acc_project_created_at: project.acc_project_created_at,
              createdOn: project.createdOn,
              createdAt: project.createdAt,
              added_at: project.added_at,
              addedAt: project.addedAt
            });
            creationDate = new Date('2022-01-01'); // Final fallback to a known default date
          }
        }
        
        // Find hub info to get subscription status
        // Robust matching against multiple ID fields
        const hubInfo = (projectsData as any)?.hubs?.find((h: any) => 
          h.id === project.hubId || 
          h.id === project.acc_hub_id || 
          h.id === project.hub_id
        );
        
        // Default to 'inactive' if not found, so we can spot data issues
        const subStatus = hubInfo?.subscriptionStatus || hubInfo?.subscription_status || 'inactive';
        
        return {
          ...project,
          number: project.id?.split('.')[1]?.substring(0, 8).toUpperCase() || 'N/A',
          defaultAccess: 'Build',
          account: project.hub_name || project.hubName,
          createdOn: creationDate.toLocaleDateString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric'
          }),
          createdDate: creationDate,
          // Preserve hasBimgoaiAccess from backend response
          hasBimgoaiAccess: project.hasBimgoaiAccess !== undefined ? project.hasBimgoaiAccess : true,
          // Add subscription status from hub info
          subscriptionStatus: subStatus
        };
      }) || [];
  }, [projectsData]);

  // Get unique accounts for filter dropdown
  const uniqueAccounts = useMemo(() => {
    const data = projectsData as any;
    if (!data?.hubs) return [];
    return (data.hubs as any[]).map(hub => ({
      name: hub.name,
      id: hub.id,
      isSubscribed: (data?.tectonixHubs || data?.bimgoaiHubs)?.some((h: any) => h.id === hub.id) || false
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [projectsData]);

  // Sorting function
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="h-4 w-4 text-blue-600" />
    ) : (
      <ArrowDown className="h-4 w-4 text-blue-600" />
    );
  };

  // Filter and sort projects
  const filteredAndSortedProjects = useMemo(() => {
    // Get active hub IDs for filtering (prefer tectonixHubs, fallback to bimgoaiHubs for compatibility)
    const activeHubIds = new Set(
      ((projectsData as any)?.tectonixHubs || (projectsData as any)?.bimgoaiHubs || []).map((h: any) => h.id)
    );

    // Debug logging for Developer Advocacy Support hub
    const devAdvocacyHubId = 'b.489c5e7a-c6c0-4212-81f3-3529a621210b';
    const devAdvocacyProjects = projects.filter((p: any) => 
      (p.hubId === devAdvocacyHubId || p.acc_hub_id === devAdvocacyHubId)
    );
    if (devAdvocacyProjects.length > 0) {
      console.log(`🔍 [FRONTEND] Developer Advocacy Support hub: ${devAdvocacyProjects.length} projects before filtering`);
      console.log(`   Projects:`, devAdvocacyProjects.map((p: any) => ({ name: p.name, id: p.id, hubId: p.hubId })));
    }

    let filtered = projects.filter(project => {
      const matchesSearch = (project.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                            (project.hubName || '').toLowerCase().includes(searchQuery.toLowerCase());
      // Multi-select account filter: if no accounts selected, show all; otherwise match selected accounts
      const matchesAccount = selectedAccounts.length === 0 || 
                            selectedAccounts.includes(project.hubName) || 
                            selectedAccounts.includes(project.account);
      
      let matchesDateRange = true;
      if (dateRange?.from || dateRange?.to) {
        if (dateRange.from && project.createdDate < dateRange.from) matchesDateRange = false;
        if (dateRange.to && project.createdDate > dateRange.to) matchesDateRange = false;
      }

      // Filter by hub subscription status (active/inactive/all)
      let matchesHubFilter = true;
      if (hubFilter !== 'all') {
        const projectHubId = project.hubId || project.acc_hub_id;
        const isActiveHub = activeHubIds.has(projectHubId);
        if (hubFilter === 'active') {
          matchesHubFilter = isActiveHub;
        } else if (hubFilter === 'inactive') {
          matchesHubFilter = !isActiveHub;
        }
      }

      return matchesSearch && matchesAccount && matchesDateRange && matchesHubFilter;
    });

    // Debug logging after filtering
    const devAdvocacyFiltered = filtered.filter((p: any) => 
      (p.hubId === devAdvocacyHubId || p.acc_hub_id === devAdvocacyHubId)
    );
    if (devAdvocacyProjects.length > 0) {
      console.log(`🔍 [FRONTEND] Developer Advocacy Support hub: ${devAdvocacyFiltered.length} projects after filtering`);
      console.log(`   Filter settings:`, {
        hubFilter,
        searchQuery,
        selectedAccounts: selectedAccounts.length,
        dateRange: dateRange ? 'set' : 'none',
        activeHubIdsCount: activeHubIds.size
      });
      if (devAdvocacyProjects.length !== devAdvocacyFiltered.length) {
        console.warn(`   ⚠️ WARNING: ${devAdvocacyProjects.length - devAdvocacyFiltered.length} projects were filtered out!`);
        const filteredOut = devAdvocacyProjects.filter(p => !devAdvocacyFiltered.includes(p));
        console.warn(`   Filtered out projects:`, filteredOut.map((p: any) => p.name));
      }
    }

    filtered.sort((a, b) => {
      // First, sort by hasBimgoaiAccess (true first)
      const aHasAccess = a.hasBimgoaiAccess !== false;
      const bHasAccess = b.hasBimgoaiAccess !== false;
      if (aHasAccess !== bHasAccess) return aHasAccess ? -1 : 1;
      
      // Then sort by selected field
      let aValue = '', bValue = '';
      
      switch (sortField) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'account':
          aValue = (a.account || '').toLowerCase();
          bValue = (b.account || '').toLowerCase();
          break;
        case 'createdOn':
          return sortDirection === 'asc' 
            ? a.createdDate.getTime() - b.createdDate.getTime() 
            : b.createdDate.getTime() - a.createdDate.getTime();
        case 'subscription':
          aValue = (a.subscriptionStatus || '').toLowerCase();
          bValue = (b.subscriptionStatus || '').toLowerCase();
          break;
      }
      
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });

    return filtered;
  }, [projects, searchQuery, selectedAccounts, dateRange, sortField, sortDirection, hubFilter, projectsData]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedProjects.length / ITEMS_PER_PAGE);
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAndSortedProjects.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredAndSortedProjects, currentPage]);

  const resetPagination = () => setCurrentPage(1);

  const handleProjectClick = (projectId: string) => {
    const selectedProject = filteredAndSortedProjects.find(p => p.id === projectId);
    if (selectedProject) {
      // Check access (prefer hasTectonixAccess, fallback to hasBimgoaiAccess for compatibility)
      const hasAccess = selectedProject.hasTectonixAccess ?? selectedProject.hasBimgoaiAccess;
      if (hasAccess === false) {
        alert(`You don't have Tectonix Access to this project.`);
        return;
      }
      
      const accProjectId = selectedProject.acc_project_id || selectedProject.id;
      
      window.localStorage.setItem('selectedProject', JSON.stringify({
        id: accProjectId,
        databaseId: selectedProject.id,
        hubId: selectedProject.hubId,
        name: selectedProject.name || 'Unnamed Project',
        hubName: selectedProject.account || 'Unknown Hub'
      }));
      
      navigate(`/dashboard/projects/${accProjectId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-lg text-gray-600">Loading your projects...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Changed width to 95% / max-1920px */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="w-[95%] max-w-[1920px] mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Building2 className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-semibold text-gray-900">AUTODESK Construction Cloud</span>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center space-x-3 hover:bg-gray-50 rounded-xl p-3 transition-all">
                  <AuthenticatedAvatar />
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <UserProfileSection />
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => logout()} className="text-red-600 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content - Changed width to 95% / max-1920px */}
      <div className="w-[95%] max-w-[1920px] mx-auto p-6">
        <div className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-light text-gray-900 mb-2">Welcome, {user?.firstName || 'User'}</h1>
            <p className="text-gray-600">What would you like to do today?</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap gap-4">
             {/* Account Filter - Multi-select with Search */}
             <div className="flex flex-col space-y-1">
                <label className="text-xs font-semibold text-gray-600 uppercase">Account</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-64 justify-between text-left font-normal text-sm bg-white">
                      <span className="truncate">
                        {selectedAccounts.length === 0 
                          ? 'All Accounts' 
                          : selectedAccounts.length === 1 
                            ? selectedAccounts[0]
                            : `${selectedAccounts.length} selected`}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0" align="start">
                    <Command>
                      <CommandInput 
                        placeholder="Search accounts..." 
                        value={accountSearchQuery}
                        onValueChange={setAccountSearchQuery}
                      />
                      <CommandList>
                        <CommandEmpty>No accounts found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            onSelect={() => {
                              if (selectedAccounts.length === uniqueAccounts.length) {
                                setSelectedAccounts([]);
                              } else {
                                setSelectedAccounts(uniqueAccounts.map(acc => acc.name));
                              }
                              resetPagination();
                            }}
                          >
                            <div className="flex items-center gap-2 w-full">
                              <Checkbox
                                checked={selectedAccounts.length === uniqueAccounts.length && uniqueAccounts.length > 0}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedAccounts(uniqueAccounts.map(acc => acc.name));
                                  } else {
                                    setSelectedAccounts([]);
                                  }
                                  resetPagination();
                                }}
                              />
                              <span className="flex-1">Select All</span>
                            </div>
                          </CommandItem>
                          {uniqueAccounts
                            .filter(acc => 
                              accountSearchQuery === '' || 
                              acc.name.toLowerCase().includes(accountSearchQuery.toLowerCase())
                            )
                            .map(acc => (
                              <CommandItem
                                key={acc.id || acc.name}
                                onSelect={() => {
                                  const newSelected = selectedAccounts.includes(acc.name)
                                    ? selectedAccounts.filter(name => name !== acc.name)
                                    : [...selectedAccounts, acc.name];
                                  setSelectedAccounts(newSelected);
                                  resetPagination();
                                }}
                              >
                                <div className="flex items-center gap-2 w-full">
                                  <Checkbox
                                    checked={selectedAccounts.includes(acc.name)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setSelectedAccounts([...selectedAccounts, acc.name]);
                                      } else {
                                        setSelectedAccounts(selectedAccounts.filter(name => name !== acc.name));
                                      }
                                      resetPagination();
                                    }}
                                  />
                                  <span className={`w-2 h-2 rounded-full ${acc.isSubscribed ? 'bg-green-500' : 'bg-gray-400'}`} />
                                  <span className="flex-1">{acc.name}</span>
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
             </div>

             {/* Date Range Filter */}
             <div className="flex flex-col space-y-1">
                <label className="text-xs font-semibold text-gray-600 uppercase">Created Date</label>
                <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-56 justify-start text-left font-normal text-sm bg-white">
                      <Calendar className="mr-2 h-4 w-4 text-blue-500" />
                      {dateRange?.from ? (
                        dateRange.to ? `${format(dateRange.from, 'MMM d')} - ${format(dateRange.to, 'MMM d')}` : format(dateRange.from, 'MMM d, yyyy')
                      ) : <span className="text-gray-500">Select date range</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="bg-white p-4 rounded-lg shadow-xl">
                       <CalendarComponent
                         mode="range"
                         month={calendarMonth}
                         onMonthChange={setCalendarMonth}
                         selected={dateRange}
                         onSelect={setDateRange}
                         numberOfMonths={1}
                       />
                       <div className="flex justify-end gap-2 mt-4">
                         <Button variant="ghost" size="sm" onClick={() => setDateRange(undefined)}>Clear</Button>
                         <Button size="sm" onClick={() => setIsDatePickerOpen(false)}>Apply</Button>
                       </div>
                    </div>
                  </PopoverContent>
                </Popover>
             </div>

             {/* Search */}
             <div className="flex flex-col space-y-1">
                <label className="text-xs font-semibold text-gray-600 uppercase">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input 
                    placeholder="Search..." 
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); resetPagination(); }}
                    className="pl-9 w-64"
                  />
                </div>
             </div>
          </div>
        </div>

        {/* Projects Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
               <div className="flex items-center gap-3">
                 <CardTitle>Your Projects ({filteredAndSortedProjects.length})</CardTitle>
                 {projectsData && (
                   <div className="text-sm text-gray-500">
                     🏢 {(projectsData as any)?.hubs?.length || 0} Hubs • ✅ {(projectsData as any)?.tectonixHubs?.length || (projectsData as any)?.bimgoaiHubs?.length || 0} Active
                   </div>
                 )}
               </div>
               <div className="flex items-center gap-3">
                 <Tabs value={hubFilter} onValueChange={(value) => {
                   setHubFilter(value as 'active' | 'inactive' | 'all');
                   resetPagination();
                 }}>
                   <TabsList className="h-9">
                     <TabsTrigger value="active" className="px-3 py-1 text-xs">Active</TabsTrigger>
                     <TabsTrigger value="inactive" className="px-3 py-1 text-xs">Inactive</TabsTrigger>
                     <TabsTrigger value="all" className="px-3 py-1 text-xs">All</TabsTrigger>
                   </TabsList>
                 </Tabs>
               <Button variant="outline" size="sm" onClick={() => setShowHubModal(true)}>
                 View Subscriptions
               </Button>
               </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase w-16">Type</th>
                    
                    <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name')}>
                      <div className="flex items-center gap-1">Name {renderSortIcon('name')}</div>
                    </th>
                    
                    <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('account')}>
                      <div className="flex items-center gap-1">Account {renderSortIcon('account')}</div>
                    </th>
                    
                    <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('createdOn')}>
                      <div className="flex items-center gap-1">Created {renderSortIcon('createdOn')}</div>
                    </th>
                    
                    {/* New Subscription Column */}
                    <th className="py-3 px-6 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('subscription')}>
                      <div className="flex items-center justify-center gap-1">Subscription {renderSortIcon('subscription')}</div>
                    </th>

                    <th className="py-3 px-6 text-center text-xs font-medium text-gray-500 uppercase">
                      Tectonix Access
                    </th>
                    
                    <th className="py-3 px-6 text-center text-xs font-medium text-gray-500 uppercase w-24">
                      Settings
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedProjects.map((project) => {
                    if (!project.id) return null;
                    // Prefer hasTectonixAccess, fallback to hasBimgoaiAccess for compatibility
                    const hasAccess = (project.hasTectonixAccess ?? project.hasBimgoaiAccess) !== false;
                    
                    return (
                      <tr 
                        key={project.id}
                        onClick={() => hasAccess && handleProjectClick(project.id)}
                        className={`transition-colors ${!hasAccess ? 'opacity-60 bg-gray-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}`}
                      >
                        <td className="py-4 px-6">
                          <div className={`w-8 h-8 rounded flex items-center justify-center ${hasAccess ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'}`}>
                            <Building2 className="h-4 w-4" />
                          </div>
                        </td>
                        
                        {/* Name Column - Removed Redundant Account Name */}
                        <td className="py-4 px-6">
                          <div className={`text-sm font-medium ${hasAccess ? 'text-gray-900' : 'text-gray-500'}`}>
                            {project.name}
                          </div>
                          {!hasAccess && <div className="text-xs text-amber-600 mt-0.5">No Access</div>}
                        </td>
                        
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {project.account}
                        </td>
                        
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {project.createdOn}
                        </td>

                        {/* New Subscription Column */}
                        <td className="py-4 px-6 text-center">
                          {(() => {
                            const status = project.subscriptionStatus?.toLowerCase() || 'inactive';
                            let badgeStyle = "bg-gray-100 text-gray-600 border-gray-200";
                            
                            if (status === 'active' || status === 'enterprise') {
                              badgeStyle = "bg-blue-100 text-blue-700 border-blue-200";
                            } else if (status === 'trial') {
                              badgeStyle = "bg-purple-100 text-purple-700 border-purple-200";
                            } else if (status === 'basic') {
                              badgeStyle = "bg-green-100 text-green-700 border-green-200";
                            } else if (status === 'expired' || status === 'inactive') {
                              badgeStyle = "bg-red-100 text-red-700 border-red-200";
                            }
                            
                            return (
                              <Badge variant="outline" className={`capitalize ${badgeStyle} font-normal`}>
                                {status}
                              </Badge>
                            );
                          })()}
                        </td>

                        {/* Tectonix Access Column */}
                        <td className="py-4 px-6 text-center">
                          {hasAccess ? (
                            <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">YES</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 border-red-200 hover:bg-red-100">NO</Badge>
                          )}
                        </td>
                        
                        <td className="py-4 px-6 text-center">
                           <Button 
                             variant="ghost" 
                             size="sm"
                             disabled={!hasAccess}
                             onClick={(e) => { 
                               e.stopPropagation(); 
                               if(hasAccess) { 
                                 setSelectedProjectForUsers(project); 
                                 setIsUserManagementOpen(true); 
                               }
                             }}
                           >
                             <Settings className="h-4 w-4 text-gray-500" />
                           </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {filteredAndSortedProjects.length === 0 && (
               <div className="text-center py-12 text-gray-500">
                 No projects found.
               </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center bg-gray-50">
                 <div className="text-sm text-gray-500">Page {currentPage} of {totalPages}</div>
                 <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Previous</Button>
                    <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</Button>
                 </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <HubSubscriptionModal 
        isOpen={showHubModal} 
        onClose={() => setShowHubModal(false)} 
        hubs={(projectsData as any)?.hubs || []} 
        tectonixHubs={(projectsData as any)?.tectonixHubs || (projectsData as any)?.bimgoaiHubs || []}
        filter={hubFilter}
      />
      {isUserManagementOpen && selectedProjectForUsers && (
        <UserManagementSidebar project={selectedProjectForUsers} onClose={() => setIsUserManagementOpen(false)} currentUser={user} />
      )}
    </div>
  );
};

// Helper Components
function AuthenticatedAvatar() {
  const { user } = useAuth();
  const letter = (user as any)?.firstName?.charAt(0) || (user as any)?.email?.charAt(0) || 'U';
  return <Avatar><AvatarFallback className="bg-blue-600 text-white">{letter.toUpperCase()}</AvatarFallback></Avatar>;
}

function UserProfileSection() {
  const { user } = useAuth();
  return (
    <div className="p-4">
      <div className="font-medium text-gray-900">{(user as any)?.firstName} {(user as any)?.lastName}</div>
      <div className="text-xs text-gray-500 mt-1">{(user as any)?.email}</div>
      <Badge variant="secondary" className="mt-2">{(user as any)?.permissionLevel || 'User'}</Badge>
    </div>
  );
}

interface UserManagementSidebarProps { project: MyHomeProject; onClose: () => void; currentUser: any; }
function UserManagementSidebar({ project, onClose, currentUser }: UserManagementSidebarProps) {
  const [isAddHubAdminOpen, setIsAddHubAdminOpen] = useState(false);
  const [isAddProjectUserOpen, setIsAddProjectUserOpen] = useState(false);
  
  // Re-implementing the original sidebar logic with cleaner structure
  const { data: userData, isLoading, refetch } = useQuery({
    queryKey: ['/api/projects', project.id, 'users'],
    queryFn: async () => {
      const response = await fetch(`/api/projects/${project.id}/users?sessionId=${localStorage.getItem('sessionId') || ''}`);
      if (!response.ok) throw new Error('Failed to fetch users');
      return response.json();
    },
    enabled: !!project.id,
  });

  return (
    <div 
      className="fixed inset-0 bg-black/40 z-50 flex justify-end backdrop-blur-sm"
      onClick={onClose}
    >
       <div 
         className="bg-white w-[450px] h-full shadow-2xl flex flex-col"
         onClick={(e) => e.stopPropagation()}
       >
          <div className="p-6 border-b flex justify-between items-center bg-gray-50">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
              <p className="text-sm text-gray-500">{project.name}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="w-5 h-5" /></Button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6 space-y-8">
             {/* Super User Section */}
             <div>
               <h3 className="text-sm font-medium text-gray-500 uppercase mb-3">Super User</h3>
               {userData?.superUser ? (
                 <div className="flex items-center gap-3 p-3 border rounded-lg bg-red-50 border-red-100">
                    <Avatar className="h-10 w-10"><AvatarFallback className="bg-red-600 text-white">SU</AvatarFallback></Avatar>
                    <div>
                      <div className="font-medium">{userData.superUser.firstName} {userData.superUser.lastName}</div>
                      <div className="text-xs text-gray-500">{userData.superUser.email}</div>
                    </div>
                 </div>
               ) : <div className="text-sm text-gray-400 italic">No super user found</div>}
             </div>

             {/* Hub Admins Section */}
             <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-medium text-gray-500 uppercase">Hub Administrators</h3>
                  <Button size="sm" variant="outline" onClick={() => setIsAddHubAdminOpen(true)}><UserPlus className="w-4 h-4 mr-2"/> Add</Button>
                </div>
                <div className="space-y-2">
                  {userData?.hubAdmins?.map((u: any) => (
                    <div key={u.id} className="flex items-center gap-3 p-3 border rounded-lg">
                      <Avatar className="h-8 w-8"><AvatarFallback className="bg-purple-600 text-white">HA</AvatarFallback></Avatar>
                      <div className="flex-1">
                        <div className="text-sm font-medium">{u.firstName} {u.lastName}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                      <Badge variant="secondary">Hub Admin</Badge>
                    </div>
                  ))}
                  {(!userData?.hubAdmins || userData.hubAdmins.length === 0) && <div className="text-sm text-gray-400 italic">No hub admins</div>}
                </div>
             </div>

             {/* Project Users Section */}
             <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-medium text-gray-500 uppercase">Project Users</h3>
                  <Button size="sm" variant="outline" onClick={() => setIsAddProjectUserOpen(true)}><UserPlus className="w-4 h-4 mr-2"/> Add</Button>
                </div>
                <div className="space-y-2">
                  {isLoading ? (
                    <div className="text-center py-4"><div className="animate-spin h-6 w-6 border-2 border-blue-600 rounded-full border-t-transparent mx-auto"/></div>
                  ) : userData?.projectUsers?.map((u: any) => (
                    <div key={u.id} className="flex items-center gap-3 p-3 border rounded-lg">
                      <Avatar className="h-8 w-8"><AvatarFallback className="bg-blue-600 text-white">PU</AvatarFallback></Avatar>
                      <div className="flex-1">
                        <div className="text-sm font-medium">{u.firstName} {u.lastName}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                      <Badge variant="outline">{u.permissionLevel || 'User'}</Badge>
                    </div>
                  ))}
                </div>
             </div>
          </div>
          
          <AddUserFromACCDialog isOpen={isAddHubAdminOpen} onClose={() => setIsAddHubAdminOpen(false)} projectId={project.id} onUserAdded={() => refetch()} existingUsers={userData} />
          <AddUserFromACCDialog isOpen={isAddProjectUserOpen} onClose={() => setIsAddProjectUserOpen(false)} projectId={project.id} onUserAdded={() => refetch()} existingUsers={userData} />
       </div>
    </div>
  )
}

function HubSubscriptionModal({ 
  isOpen, 
  onClose, 
  hubs, 
  tectonixHubs, 
  filter 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  hubs: any[]; 
  tectonixHubs: any[]; 
  filter: 'active' | 'inactive' | 'all';
}) {
  // Filter hubs based on the selected filter
  const filteredHubs = React.useMemo(() => {
    if (filter === 'all') {
      return hubs;
    }
    
    const activeHubIds = new Set(tectonixHubs.map((h: any) => h.id));
    
    if (filter === 'active') {
      return hubs.filter((hub: any) => activeHubIds.has(hub.id));
    } else {
      // filter === 'inactive'
      return hubs.filter((hub: any) => !activeHubIds.has(hub.id));
    }
  }, [hubs, tectonixHubs, filter]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Hub Subscriptions</DialogTitle></DialogHeader>
        <div className="space-y-2 mt-4 max-h-[60vh] overflow-y-auto">
          {filteredHubs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No {filter === 'active' ? 'active' : filter === 'inactive' ? 'inactive' : ''} hubs found.
            </div>
          ) : (
            filteredHubs.map((hub: any) => {
             const isActive = tectonixHubs.some((h:any) => h.id === hub.id);
             return (
               <div key={hub.id} className="flex justify-between items-center p-3 border rounded hover:bg-gray-50">
                 <div className="flex flex-col">
                   <span className="font-medium">{hub.name}</span>
                   <span className="text-xs text-gray-400">{hub.id}</span>
                 </div>
                 <Badge variant={isActive ? "default" : "secondary"} className={isActive ? "bg-green-600 hover:bg-green-700" : ""}>
                   {isActive ? "Active" : "Inactive"}
                 </Badge>
               </div>
             )
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default MyHomePage;